import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

type Theme = 'light' | 'dark' | 'system';

type ThemeContextValue = { theme: Theme; effectiveTheme: 'light' | 'dark'; setTheme: (theme: Theme) => void };

const ThemeContext = createContext<ThemeContextValue | null>(null);

function systemTheme(): 'light' | 'dark' {
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => (localStorage.getItem('a3gs-theme') as Theme | null) ?? 'system');
  const [system, setSystem] = useState<'light' | 'dark'>(() => systemTheme());
  const effectiveTheme = theme === 'system' ? system : theme;

  useEffect(() => {
    localStorage.setItem('a3gs-theme', theme);
    document.documentElement.dataset.theme = effectiveTheme;
  }, [theme, effectiveTheme]);

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: light)');
    const handler = () => setSystem(systemTheme());
    media.addEventListener('change', handler);
    return () => media.removeEventListener('change', handler);
  }, []);

  const value = useMemo(() => ({ theme, effectiveTheme, setTheme }), [theme, effectiveTheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const value = useContext(ThemeContext);
  if (!value) throw new Error('useTheme deve ser usado dentro do ThemeProvider.');
  return value;
}
